import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDistanceToNow } from "date-fns";
import { Megaphone } from "lucide-react";
import DOMPurify from "dompurify";
import type { Update } from "@shared/schema";

interface UpdatesFeedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpdatesFeedDialog({ open, onOpenChange }: UpdatesFeedDialogProps) {
  const { data: updates = [], isLoading } = useQuery<Update[]>({
    queryKey: ["/api/updates"],
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto flex flex-col" data-testid="dialog-updates-feed">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Updates & Announcements
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2" data-testid="updates-feed-content">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : updates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Megaphone className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No updates yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Check back later for announcements and updates
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {updates.map((update) => (
                <div 
                  key={update.id} 
                  className="p-4 border border-border rounded-lg bg-card hover:shadow-sm transition-shadow"
                  data-testid={`update-${update.id}`}
                >
                  {update.contentHtml ? (
                    <div 
                      className="prose prose-sm dark:prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.contentHtml) }}
                      data-testid={`update-content-html-${update.id}`}
                    />
                  ) : (
                    <p className="text-foreground whitespace-pre-wrap" data-testid={`update-content-text-${update.id}`}>
                      {update.text}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2">
                    {formatDistanceToNow(new Date(update.createdAt), { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
